/**
 * ConferenciaCaixaListPage
 *
 * Listagem de caixas para conferência — UI alinhada ao padrão Contas a Pagar (ERPTable + toolbar).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileSearchCorner,
  Search,
  Filter,
  Calendar,
  X,
  Check,
  CheckCircle2,
  AlertCircle,
  Wallet
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import ERPTable from '../../../../components/ERP/ERPTable'
import { cashRegisterService } from '../../../../services/erpApi'
import './ConferenciaCaixaListPage.css'

const STATUS_OPTIONS = [
  { id: 'open', label: 'Aberto', icon: AlertCircle },
  { id: 'closed', label: 'Fechado', icon: CheckCircle2 }
]

const RECONCILED_OPTIONS = [
  { id: '', label: 'Todos', icon: Wallet },
  { id: 'yes', label: 'Conferido', icon: CheckCircle2 },
  { id: 'no', label: 'Pendente', icon: AlertCircle }
]

function formatDateTime(date) {
  if (!date) return '-'
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatCaixaCode(row) {
  const day = row.opening_date ? new Date(row.opening_date).getDate() : 0
  return `${row.id}.${day}`
}

function ConferenciaCaixaListPage() {
  const navigate = useNavigate()
  const periodBtnRef = useRef(null)

  const [caixas, setCaixas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    dateFrom: '',
    dateTo: '',
    reconciled: ''
  })
  const [draftFilters, setDraftFilters] = useState(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const loadCaixas = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      }
      if (filters.status.length === 1) {
        params.status = filters.status[0]
      }
      if (filters.dateFrom) params.dateFrom = filters.dateFrom
      if (filters.dateTo) params.dateTo = filters.dateTo

      const response = await cashRegisterService.getReconciliationList(params)

      if (response.success) {
        setCaixas(response.data || [])
        if (response.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar caixas:', error)
      alert('Erro ao carregar caixas. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }, [filters.status, filters.dateFrom, filters.dateTo, pagination.page, pagination.limit])

  useEffect(() => {
    loadCaixas()
  }, [loadCaixas])

  const displayedCaixas = useMemo(() => {
    const term = filters.search.trim().toLowerCase()
    return caixas.filter((row) => {
      if (term) {
        const code = formatCaixaCode(row).toLowerCase()
        const matchesSearch =
          String(row.id).includes(term) ||
          code.includes(term) ||
          (row.opened_by_name || '').toLowerCase().includes(term)
        if (!matchesSearch) return false
      }
      if (filters.reconciled === 'yes' && !row.is_reconciled) return false
      if (filters.reconciled === 'no' && row.is_reconciled) return false
      return true
    })
  }, [caixas, filters.search, filters.reconciled])

  const hasDateRangeFilter = Boolean(filters.dateFrom && filters.dateTo)

  const hasActiveFilters = useMemo(() => {
    return (
      filters.status.length > 0 ||
      filters.reconciled !== '' ||
      hasDateRangeFilter
    )
  }, [filters.status, filters.reconciled, hasDateRangeFilter])

  const handlePeriodRangeChange = (start, end) => {
    let from = start
    let to = end
    if (from && to && from > to) {
      ;[from, to] = [to, from]
    }
    setFilters((prev) => ({
      ...prev,
      dateFrom: from,
      dateTo: to
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleClearPeriodFilter = () => {
    setFilters((prev) => ({
      ...prev,
      dateFrom: '',
      dateTo: ''
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const resetFilters = () => {
    setFilters({
      search: '',
      status: [],
      dateFrom: '',
      dateTo: '',
      reconciled: ''
    })
    setDraftFilters(null)
    setIsFilterOpen(false)
    setIsPeriodOpen(false)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const openFilterSidebar = () => {
    setDraftFilters({
      status: [...filters.status],
      reconciled: filters.reconciled
    })
    setIsFilterOpen(true)
  }

  const toggleDraftStatus = (statusValue) => {
    setDraftFilters((prev) => {
      const current = prev?.status || []
      const isSelected = current.includes(statusValue)
      const nextStatus = isSelected
        ? current.filter((s) => s !== statusValue)
        : [...current, statusValue]
      return { ...prev, status: nextStatus }
    })
  }

  const applySidebarFilters = () => {
    if (!draftFilters) return
    setFilters((prev) => ({
      ...prev,
      status: draftFilters.status,
      reconciled: draftFilters.reconciled
    }))
    setPagination((prev) => ({ ...prev, page: 1 }))
    setIsFilterOpen(false)
    setDraftFilters(null)
  }

  const handleConferir = (caixa) => {
    navigate(`/erp/vendas/conferencia-caixa/${caixa.id}`)
  }

  const getStatusBadge = (status) => {
    if (status === 'open') {
      return <span className="conferencia-caixa-status-badge open">Aberto</span>
    }
    if (status === 'closed') {
      return <span className="conferencia-caixa-status-badge closed">Fechado</span>
    }
    return <span className="conferencia-caixa-status-badge pending">{status}</span>
  }

  const getReconciledBadge = (isReconciled) => {
    if (isReconciled) {
      return (
        <span className="conferencia-caixa-status-badge reconciled" style={{ gap: '0.25rem' }}>
          <Check size={12} />
          Conferido
        </span>
      )
    }
    return <span className="conferencia-caixa-status-badge pending">Pendente</span>
  }

  const columns = [
    {
      key: 'id',
      label: 'Código',
      sortable: true,
      render: (_, row) => <code className="conferencia-caixa-code">{formatCaixaCode(row)}</code>
    },
    {
      key: 'opened_by_name',
      label: 'Operador',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'opening_date',
      label: 'Abertura',
      sortable: true,
      render: (value) => formatDateTime(value)
    },
    {
      key: 'closing_date',
      label: 'Fechamento',
      sortable: true,
      render: (value) => formatDateTime(value)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'is_reconciled',
      label: 'Conferência',
      align: 'center',
      render: (value) => getReconciledBadge(value)
    },
    {
      key: 'actions',
      label: '',
      align: 'center',
      headerClassName: 'erp-table-actions-col',
      className: 'erp-table-actions-col',
      render: (_, row) => (
        <div className="erp-table-actions-cell">
          {row.status === 'closed' && (
            <button
              type="button"
              className="erp-table-row-menu-btn"
              onClick={(e) => {
                e.stopPropagation()
                handleConferir(row)
              }}
              title="Conferir caixa"
            >
              <FileSearchCorner size={16} />
            </button>
          )}
        </div>
      )
    }
  ]

  const sidebarStatus = draftFilters?.status || []
  const sidebarReconciled = draftFilters?.reconciled ?? ''

  return (
    <div className="conferencia-caixa-page">
      <div className="page-header">
        <div>
          <h1>Conferência de Caixa</h1>
          <p>Listagem de caixas para conferência</p>
        </div>
      </div>

      <div className="conferencia-caixa-toolbar">
        <div className="conferencia-caixa-search-wrapper">
          <Search className="conferencia-caixa-search-icon" size={18} />
          <input
            type="text"
            placeholder="Pesquisar operador ou código..."
            className="conferencia-caixa-search-input"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>
        <div className="conferencia-caixa-table-actions">
          <button
            type="button"
            onClick={openFilterSidebar}
            className={`conferencia-caixa-filter-button ${hasActiveFilters ? 'active' : ''}`}
          >
            <Filter size={14} /> Filtros
            {hasActiveFilters && <span className="conferencia-caixa-filter-badge" />}
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className={`conferencia-caixa-filter-button ${hasDateRangeFilter ? 'active' : ''}`}
            title="Filtrar por período de abertura"
          >
            <Calendar size={14} /> Período
            {hasDateRangeFilter && <span className="conferencia-caixa-filter-badge" />}
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={filters.dateFrom}
            endValue={filters.dateTo}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Abertura"
            subtitle="Início e Fim"
          />
        </div>
      </div>

      <ERPTable
        columns={columns}
        data={displayedCaixas}
        loading={loading}
        emptyMessage="Nenhum caixa encontrado"
        loadingMessage="Carregando caixas..."
        onRowClick={(row) => {
          if (row.status === 'closed') handleConferir(row)
        }}
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: (page) => setPagination((prev) => ({ ...prev, page })),
          showTotalCount: true
        }}
      />

      {isFilterOpen && draftFilters && (
        <>
          <div className="conferencia-caixa-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="conferencia-caixa-filter-sidebar conferencia-caixa-scrollbar">
            <div className="conferencia-caixa-filter-header">
              <div className="conferencia-caixa-filter-header-content">
                <div className="conferencia-caixa-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="conferencia-caixa-filter-header-title">Filtros</h3>
                  <p className="conferencia-caixa-filter-header-subtitle">Refine a listagem</p>
                </div>
              </div>
              <button
                type="button"
                className="conferencia-caixa-filter-close"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Fechar filtros"
              >
                <X size={20} />
              </button>
            </div>

            <div className="conferencia-caixa-filter-content">
              <div className="conferencia-caixa-filter-section">
                <h4 className="conferencia-caixa-filter-section-title">Status (Selecione múltiplos)</h4>
                <div className="conferencia-caixa-filter-options">
                  {STATUS_OPTIONS.map((opt) => {
                    const isSelected = sidebarStatus.includes(opt.id)
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleDraftStatus(opt.id)}
                        className={`conferencia-caixa-filter-option ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="conferencia-caixa-filter-option-content">
                          <opt.icon size={18} className="conferencia-caixa-filter-option-icon" />
                          <span className="conferencia-caixa-filter-option-label">{opt.label}</span>
                        </div>
                        {isSelected && <Check size={16} className="conferencia-caixa-filter-option-check" />}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="conferencia-caixa-filter-section">
                <h4 className="conferencia-caixa-filter-section-title">Conferência</h4>
                <div className="conferencia-caixa-filter-options">
                  {RECONCILED_OPTIONS.map((opt) => (
                    <button
                      key={opt.id || 'all'}
                      type="button"
                      onClick={() => setDraftFilters((prev) => ({ ...prev, reconciled: opt.id }))}
                      className={`conferencia-caixa-filter-option ${sidebarReconciled === opt.id ? 'selected' : ''}`}
                    >
                      <div className="conferencia-caixa-filter-option-content">
                        <opt.icon size={18} className="conferencia-caixa-filter-option-icon" />
                        <span className="conferencia-caixa-filter-option-label">{opt.label}</span>
                      </div>
                      {sidebarReconciled === opt.id && (
                        <Check size={16} className="conferencia-caixa-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="conferencia-caixa-filter-footer">
              <button type="button" className="conferencia-caixa-filter-reset" onClick={resetFilters}>
                Limpar
              </button>
              <button type="button" className="conferencia-caixa-filter-apply" onClick={applySidebarFilters}>
                Aplicar
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  )
}

export default ConferenciaCaixaListPage
